import ELK from 'elkjs/lib/elk.bundled.js';
import type { ElkNode, ElkExtendedEdge } from 'elkjs/lib/elk-api';

// Left→right layered layout for the value-graph spine, via elkjs partitioning.
//
// Each spine node is pinned to a column (elk "partition") by its type —
// idea (0) → capability (1) → challenge/opportunity (2) → job (3) → customer
// (4) — so arrows flow left→right: the idea seed on the left, the JTBD fanning
// rightward into the customer anchor (job → customer). Partitioning forces
// those columns by type rather than leaning on edge direction (the idea→capability
// `comprises` edge now gives capability an incoming spine edge, but the columns
// are still type-driven, not edge-derived) while elk still minimises crossings.
//
// Overlay node types (market_signal, assumption) are excluded from the elk
// spine layout and placed in horizontal bands split by type across the spine —
// market signals above it, assumptions below — so the vertical axis reads:
// external evidence above, PM hypotheses below. Each overlay anchors over/under
// the specific spine node it references (short vertical dashed drop), fanning
// horizontally (and wrapping to further rows) when several share an anchor.

export interface LayoutNodeInput {
  id: string;
  type: string;
  // Optional ordering hint. The layout never interprets it — it only keeps
  // components that share a key adjacent (see groupComponents). Callers pass
  // the respondent's email so one respondent's clusters read as one block.
  group?: string;
}

export interface LayoutEdgeInput {
  source: string;
  target: string;
}

export interface Position {
  x: number;
  y: number;
}

const SPINE_RANK: Record<string, number> = {
  idea: 0,
  capability: 1,
  challenge: 2,
  opportunity: 2,
  job: 3,
  customer: 4,
};

const NODE_WIDTH = 240;
const NODE_HEIGHT = 96;
// Strict column stride for the final x-coordinate. We pin x ourselves (rank ×
// gap) rather than trusting elk's x: partitioning only guarantees column
// *order*, not one layer per type, so an oddly-connected node (e.g. a lone
// opportunity→job with no challenge) lands in its own sub-layer and drifts
// right. Forcing x keeps every node of a type exactly column-aligned; we keep
// elk's y for crossing-minimised vertical ordering.
//
// Node width (240) plus a wide gap so bézier edges and their labels have room:
// the spine fans many challenges and opportunities into few jobs, so the gap is
// where all the crossing traffic and every edge label has to fit. Costs nothing
// to widen — fit-to-view scales by min(vw/W, vh/H), and the spine is far taller
// than it is wide, so height stays the binding axis. A taste knob, tune freely.
const COLUMN_GAP = 1120;
// Minimum vertical centre-to-centre gap between two nodes in the same column.
// Because we pin x by rank (above), elk's no-overlap guarantee — which holds in
// *elk's* x — no longer applies: two same-rank nodes elk split into different
// sub-layers can each sit at y≈0 in their own layer and collide once forced to
// one column. deoverlapColumns re-spaces each column to at least this stride.
export const ROW_STRIDE = NODE_HEIGHT + 60; // node height + elk's nodeNode spacing

// Overlay bands, split by type across the spine: evidence above, hypotheses
// below. Within a band, a dense fan wraps into further rows away from the spine.
export const OVERLAY_SIDE: Record<string, 'above' | 'below'> = {
  market_signal: 'above',
  assumption: 'below',
};
// Top-left-to-top-left offset from the spine's outermost row to the first
// overlay row. Deliberately NOT adjusted by NODE_HEIGHT on either side: since
// nodes hang down from their y, the resulting visual edge gap is
// OVERLAY_GUTTER - NODE_HEIGHT (124px) both above and below the spine —
// adding the height to the below band would break that symmetry.
export const OVERLAY_GUTTER = 220;
export const OVERLAY_BAND = 200; // vertical gap between wrapped rows within a band
export const OVERLAY_X_GAP = 280; // horizontal centre-to-centre stride within a row
export const MAX_FAN_PER_ROW = 4; // fan wraps to a further row beyond this

export function spineRank(type: string): number | null {
  return Object.prototype.hasOwnProperty.call(SPINE_RANK, type) ? SPINE_RANK[type] : null;
}

// Place overlay nodes in per-anchor fans: each overlay sits directly over
// (market_signal) or under (assumption) the spine node it references, so its
// dashed edge is a short vertical drop rather than a long diagonal. Overlays
// sharing an anchor fan horizontally, centred on the anchor, wrapping to a
// further row beyond MAX_FAN_PER_ROW. A final left-to-right sweep per band-row
// de-overlaps adjacent anchors' fans and clamps the leftmost fan so it doesn't
// run far off the graph's left edge. Mutates positions. Exported for tests.
export function placeOverlays(
  nodes: LayoutNodeInput[],
  edges: LayoutEdgeInput[],
  positions: Map<string, Position>,
): void {
  const overlays = nodes.filter((n) =>
    Object.prototype.hasOwnProperty.call(OVERLAY_SIDE, n.type),
  );
  if (overlays.length === 0) return;

  const overlayIds = new Set(overlays.map((o) => o.id));
  // First outgoing edge target per overlay — the thing it's evidence for.
  const targetOf = new Map<string, string>();
  for (const e of edges) {
    if (overlayIds.has(e.source) && !targetOf.has(e.source)) targetOf.set(e.source, e.target);
  }

  // Resolve to the positioned spine node ultimately referenced, walking
  // overlay→overlay links (e.g. assumption → market_signal → job). Null when
  // the chain dangles — those fall back to the spine's horizontal centre.
  const resolveAnchor = (id: string, seen: Set<string>): string | null => {
    const target = targetOf.get(id);
    if (!target) return null;
    if (positions.has(target)) return target;
    if (seen.has(target)) return null;
    seen.add(target);
    return resolveAnchor(target, seen);
  };

  const xs = [...positions.values()].map((p) => p.x);
  const ys = [...positions.values()].map((p) => p.y);
  const centerX = xs.length ? (Math.min(...xs) + Math.max(...xs)) / 2 : 0;
  const minX = xs.length ? Math.min(...xs) : 0;
  const spineTop = ys.length ? Math.min(...ys) : 0;
  const spineBottom = ys.length ? Math.max(...ys) : 0;

  // Group by (band side, anchor); deterministic order within a fan.
  const fans = new Map<string, { side: 'above' | 'below'; anchorX: number; members: string[] }>();
  for (const o of overlays) {
    const side = OVERLAY_SIDE[o.type];
    const anchor = resolveAnchor(o.id, new Set([o.id]));
    const key = `${side}|${anchor ?? ''}`;
    const anchorX = anchor ? positions.get(anchor)!.x : centerX;
    const fan = fans.get(key) ?? fans.set(key, { side, anchorX, members: [] }).get(key)!;
    fan.members.push(o.id);
  }

  // Desired placement per overlay: rows of MAX_FAN_PER_ROW centred on the
  // anchor, rows stacking away from the spine.
  const byBandRow = new Map<string, { id: string; x: number; y: number }[]>();
  for (const fan of fans.values()) {
    fan.members.sort();
    for (let i = 0; i < fan.members.length; i++) {
      const row = Math.floor(i / MAX_FAN_PER_ROW);
      const col = i % MAX_FAN_PER_ROW;
      const inRow = Math.min(fan.members.length - row * MAX_FAN_PER_ROW, MAX_FAN_PER_ROW);
      const x = fan.anchorX - ((inRow - 1) * OVERLAY_X_GAP) / 2 + col * OVERLAY_X_GAP;
      const y =
        fan.side === 'above'
          ? spineTop - OVERLAY_GUTTER - row * OVERLAY_BAND
          : spineBottom + OVERLAY_GUTTER + row * OVERLAY_BAND;
      const rowKey = `${fan.side}|${row}`;
      (byBandRow.get(rowKey) ?? byBandRow.set(rowKey, []).get(rowKey)!).push({
        id: fan.members[i],
        x,
        y,
      });
    }
  }

  // Sweep each band-row left→right: preserves anchor order, pushes colliding
  // fans apart, and floors the first x at the leftmost spine column so a wide
  // fan over the idea can't run far off-graph.
  for (const row of byBandRow.values()) {
    row.sort((a, b) => a.x - b.x || a.id.localeCompare(b.id));
    let prevX = minX - OVERLAY_X_GAP;
    for (const p of row) {
      const x = Math.max(p.x, prevX + OVERLAY_X_GAP);
      positions.set(p.id, { x, y: p.y });
      prevX = x;
    }
  }
}

// Pure: build the elk graph for the spine. Overlay nodes are dropped; edges are
// kept only when both ends are spine nodes. Each node carries its column as an
// elk partition. Exported for unit testing without running the layout engine.
export function buildElkGraph(nodes: LayoutNodeInput[], edges: LayoutEdgeInput[]): ElkNode {
  const rankOf = new Map<string, number>();
  for (const n of nodes) {
    const r = spineRank(n.type);
    if (r !== null) rankOf.set(n.id, r);
  }

  const children: ElkNode[] = nodes
    .filter((n) => rankOf.has(n.id))
    .map((n) => ({
      id: n.id,
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
      layoutOptions: { 'elk.partitioning.partition': String(rankOf.get(n.id)) },
    }));

  const elkEdges: ElkExtendedEdge[] = edges
    .filter((e) => rankOf.has(e.source) && rankOf.has(e.target))
    .map((e, i) => ({ id: `edge-${i}`, sources: [e.source], targets: [e.target] }));

  return {
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': 'RIGHT',
      'elk.partitioning.activate': 'true',
      'elk.layered.spacing.nodeNodeBetweenLayers': '140',
      'elk.spacing.nodeNode': '60',
      // Lay every connected component out in one shared coordinate space. By
      // default elk lays each component out on its own and packs them side by
      // side, which puts nodes of the same rank at different x — and since we
      // then pin x by rank, deoverlapColumns merges those components by sorting
      // on a y they never shared, scrambling elk's ordering into an arbitrary
      // interleave. The spine is routinely disconnected (challenges and
      // opportunities from different dialogs feed disjoint sets of jobs), so
      // this is the common case, not an edge case.
      'elk.separateConnectedComponents': 'false',
    },
    children,
    edges: elkEdges,
  };
}

// Guarantee no two nodes in the same column overlap vertically. elk's y is a
// good *ordering* (crossing-minimised) but not a usable spacing once we collapse
// elk's sub-layers into one pinned column — see ROW_STRIDE. Per column, sort by
// elk's y and sweep top→down, pushing any node that's too close to its
// predecessor down to prev + ROW_STRIDE. Preserves elk's relative order and only
// nudges when needed. Mutates the given positions map in place. Exported for tests.
export function deoverlapColumns(
  positions: Map<string, Position>,
  rankById: Map<string, number | null>,
): void {
  const byRank = new Map<number, string[]>();
  for (const id of positions.keys()) {
    const rank = rankById.get(id);
    if (rank == null) continue; // overlays are placed separately
    (byRank.get(rank) ?? byRank.set(rank, []).get(rank)!).push(id);
  }
  for (const ids of byRank.values()) {
    ids.sort((a, b) => positions.get(a)!.y - positions.get(b)!.y);
    let prevY = -Infinity;
    for (const id of ids) {
      const pos = positions.get(id)!;
      const y = Math.max(pos.y, prevY + ROW_STRIDE);
      positions.set(id, { x: pos.x, y });
      prevY = y;
    }
  }
}

// Keep each connected component of the spine contiguous, and in the same
// relative order in every column it touches.
//
// Before synthesis the spine is one disjoint cluster per respondent: nothing
// links one respondent's challenge to another's job. elk's crossing sweep is a
// barycentre heuristic, so disconnected components exert no force on each other
// — interleaving them is locally crossing-neutral and it has no gradient to
// separate them. They end up wherever the input order left them, which is
// DynamoDB SK order, which is UUID order. Reading down a column then alternates
// between respondents for no structural reason.
//
// Two components can only produce a crossing if their index ranges interleave
// in one column while their edges run to the other in a different order. Making
// every component a contiguous block, ordered identically in both columns,
// removes every cross-component crossing by construction and leaves only the
// crossings inherent to one component's own fan.
//
// Nodes are permuted into the y slots elk already chose rather than re-spaced,
// so the column's height, its internal gaps and elk's within-component ordering
// all survive untouched — this only changes which node sits in which slot.
//
// `groupById` is an optional ordering hint (callers pass the respondent). One
// respondent's nodes usually form several disjoint clusters, because they hang
// off different jobs that aren't linked to each other — so component grouping
// alone still reads as respondent A, B, A. The hint sorts components sharing a
// key next to each other, which merges those runs.
//
// It is deliberately a tiebreak on component *order*, never the partition
// itself. Partitioning by respondent would split a component once synthesis
// links two respondents' nodes together, and splitting a component adds
// crossings rather than removing them. Ordering can never do that.
//
// Mutates positions. No-op on a fully connected spine (i.e. after synthesis).
// Exported for tests.
export function groupComponents(
  positions: Map<string, Position>,
  rankById: Map<string, number | null>,
  edges: LayoutEdgeInput[],
  groupById?: Map<string, string | undefined>,
): void {
  const ranked = [...positions.keys()].filter((id) => rankById.get(id) != null);
  if (ranked.length === 0) return;

  // Union-find over spine nodes, joined by spine edges.
  const parent = new Map<string, string>(ranked.map((id) => [id, id]));
  const find = (x: string): string => {
    let root = x;
    while (parent.get(root) !== root) root = parent.get(root)!;
    while (parent.get(x) !== root) {
      const next = parent.get(x)!;
      parent.set(x, root);
      x = next;
    }
    return root;
  };
  for (const e of edges) {
    if (!parent.has(e.source) || !parent.has(e.target)) continue;
    const a = find(e.source);
    const b = find(e.target);
    if (a !== b) parent.set(a, b);
  }

  // Order components by their mean y so the grouped layout stays close to the
  // one elk produced; ties broken on the smallest member id for determinism.
  const members = new Map<string, string[]>();
  for (const id of ranked) {
    const root = find(id);
    (members.get(root) ?? members.set(root, []).get(root)!).push(id);
  }
  if (members.size <= 1) return;

  const meanY = (ids: string[]) => ids.reduce((a, id) => a + positions.get(id)!.y, 0) / ids.length;
  // A component's hint is the smallest key among its members — deterministic,
  // and unambiguous while a component belongs to one respondent. A component
  // spanning several (post-synthesis) picks one arbitrarily, which only affects
  // where the block sits, not whether it stays whole. Keyless components sort
  // last, so synthesised nodes gather at the bottom rather than splitting the
  // respondent blocks.
  const NO_KEY = '￿';
  const hint = (ids: string[]) =>
    ids.reduce((best, id) => {
      const k = groupById?.get(id) ?? NO_KEY;
      return k < best ? k : best;
    }, NO_KEY);
  const keyed = [...members.entries()].map(
    ([root, ids]) => [root, hint(ids), meanY(ids)] as const,
  );
  const order = new Map<string, number>(
    keyed
      .sort(
        ([ra, ka, ya], [rb, kb, yb]) =>
          (ka < kb ? -1 : ka > kb ? 1 : 0) || ya - yb || (ra < rb ? -1 : 1),
      )
      .map(([root], i) => [root, i]),
  );

  // Per column: reassign the same set of y slots, now in component order.
  const byRank = new Map<number, string[]>();
  for (const id of ranked) {
    const rank = rankById.get(id)!;
    (byRank.get(rank) ?? byRank.set(rank, []).get(rank)!).push(id);
  }
  for (const ids of byRank.values()) {
    const slots = ids.map((id) => positions.get(id)!.y).sort((a, b) => a - b);
    const ordered = [...ids].sort(
      (a, b) =>
        order.get(find(a))! - order.get(find(b))! ||
        positions.get(a)!.y - positions.get(b)!.y ||
        (a < b ? -1 : 1),
    );
    ordered.forEach((id, i) => positions.set(id, { x: positions.get(id)!.x, y: slots[i] }));
  }
}

// Each column's vertical anchor after the x-pinning collapse is an artifact,
// not a decision: elk's y is kept only as a within-column ordering, and
// deoverlapColumns only ever pushes nodes down — so a whole column (all the
// jobs; the lone idea) can drift to the bottom of the canvas with its edges
// bunched into a corner. Align columns as rigid blocks instead: anchor the
// tallest column (it defines the canvas's vertical mass), then sweep outward,
// shifting each column by the least-squares offset against its already-placed
// neighbours — mean of (neighbourY − ownY) over the edges that connect them
// (a singleton column degenerates to sitting at its neighbours' barycentre).
// A column with no edges toward the placed side centres on the placed extent's
// midline. Rigid shifts preserve elk's internal ordering and the deoverlap
// strides, so no new overlaps. Mutates in place. Exported for tests.
export function alignColumns(
  positions: Map<string, Position>,
  rankById: Map<string, number | null>,
  edges: LayoutEdgeInput[],
): void {
  const byRank = new Map<number, string[]>();
  for (const id of positions.keys()) {
    const rank = rankById.get(id);
    if (rank == null) continue;
    (byRank.get(rank) ?? byRank.set(rank, []).get(rank)!).push(id);
  }
  if (byRank.size <= 1) return;

  const span = (ids: string[]) => {
    const ys = ids.map((id) => positions.get(id)!.y);
    return Math.max(...ys) - Math.min(...ys);
  };
  // Anchor: most nodes, then largest span, then leftmost — deterministic.
  const ranks = [...byRank.keys()].sort((a, b) => a - b);
  let anchor = ranks[0];
  for (const r of ranks) {
    const cand = byRank.get(r)!;
    const best = byRank.get(anchor)!;
    if (cand.length > best.length || (cand.length === best.length && span(cand) > span(best))) {
      anchor = r;
    }
  }

  const placed = new Set<number>([anchor]);
  const sweep = ranks
    .filter((r) => r !== anchor)
    .sort((a, b) => Math.abs(a - anchor) - Math.abs(b - anchor) || a - b);

  for (const r of sweep) {
    const ids = byRank.get(r)!;
    const idSet = new Set(ids);

    // Least-squares rigid shift against the already-placed side.
    const deltas: number[] = [];
    for (const e of edges) {
      const inside = idSet.has(e.source) ? e.source : idSet.has(e.target) ? e.target : null;
      if (!inside) continue;
      const outside = inside === e.source ? e.target : e.source;
      const outRank = rankById.get(outside);
      if (outRank == null || !placed.has(outRank)) continue; // overlay edges and unplaced columns don't pull
      const outPos = positions.get(outside);
      if (outPos) deltas.push(outPos.y - positions.get(inside)!.y);
    }

    let shift: number;
    if (deltas.length > 0) {
      shift = deltas.reduce((a, b) => a + b, 0) / deltas.length;
    } else {
      const placedYs = [...positions.entries()]
        .filter(([id]) => {
          const pr = rankById.get(id);
          return pr != null && placed.has(pr);
        })
        .map(([, p]) => p.y);
      const ys = ids.map((id) => positions.get(id)!.y);
      shift =
        (Math.min(...placedYs) + Math.max(...placedYs)) / 2 - (Math.min(...ys) + Math.max(...ys)) / 2;
    }

    for (const id of ids) {
      const pos = positions.get(id)!;
      positions.set(id, { x: pos.x, y: pos.y + shift });
    }
    placed.add(r);
  }
}

const elk = new ELK();

// Async: run elk over the spine and return computed top-left positions by node
// id. Empty map when there are no spine nodes (caller leaves positions as-is).
export async function computeSpineLayout(
  nodes: LayoutNodeInput[],
  edges: LayoutEdgeInput[],
): Promise<Map<string, Position>> {
  const graph = buildElkGraph(nodes, edges);
  if (!graph.children || graph.children.length === 0) return new Map();

  const laidOut = await elk.layout(graph);
  const rankById = new Map(nodes.map((n) => [n.id, spineRank(n.type)]));
  const positions = new Map<string, Position>();
  for (const child of laidOut.children ?? []) {
    if (typeof child.y !== 'number') continue;
    // x is pinned to the column (rank × gap), not elk's computed x — see COLUMN_GAP.
    const rank = rankById.get(child.id);
    const x = rank != null ? rank * COLUMN_GAP : (child.x ?? 0);
    positions.set(child.id, { x, y: child.y });
  }
  // Pinning x by rank collapses elk's sub-layers into one column, which can
  // re-introduce the vertical overlaps elk had avoided in its own x — fix them
  // before placing overlays so the overlay bands sit beyond the real spine
  // extent.
  // Before de-overlapping: grouping only permutes nodes between the y slots elk
  // chose, so it can't introduce an overlap, but it must happen while those
  // slots are still elk's own.
  groupComponents(
    positions,
    rankById,
    edges,
    new Map(nodes.map((n) => [n.id, n.group])),
  );
  deoverlapColumns(positions, rankById);
  alignColumns(positions, rankById, edges);
  placeOverlays(nodes, edges, positions);
  return positions;
}
